import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ApAlertService } from 'webcommon/core-ui';
import { FcGenericItemModalBaseComponent } from '../../generic/fc-generic-item-modal-base.component';
import { FcListValidationService, FormGroupArray } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FcInsurancePlanListItemDto, FcModifyInsurancePlanListItemDto, FormGroupValidationRules } from 'webcommon/shared';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-insurance-plan-list-modal',
  templateUrl: './fc-insurance-plan-list-modal.component.html',
})
export class FcInsurancePlanListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {
  public override baseId = 'fc-insurance-plan-item';

  // List Item with default value
  public listItem: FcInsurancePlanListItemDto = {
    InsurancePlanUid: '',
    Id: '',
    InsuranceCarrierUid: '',
    Inactive: false,
    Name: '',
    ExternalID: '',
    UseCarrierAddress: true,
    Address: {
      ZipUid: '',
      CountyName: '',
      CountyCode: '',
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Zip: '',
      Country: ''
    },
    Phone1: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Phone2: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Phone3: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Phone4: {
      PhoneNumber: '',
      PhoneType: '',
      PhoneTypeUid: ''
    },
    Notes: '',
    UseInsuranceCarrierAccountType: true,
    AccountTypeUid: '',
    UseCarrierFeeSchedule: true,
    UseProviderFeeSchedule: false,
    FeeScheduleUid: '',
    UseCarrierAmountAllowedFeeSchedule: true,
    AmountAllowedFeeScheduleUid: '',
    UseCarrierPaperClaimFormat: true,
    PaperClaimFormatUid: '',
    UseCarrierElectronicClaimFormat: true,
    ElectronicClaimFormatUid: '',
    UseCarrierPaperClaimDefaultOption: true,
    PaperClaimDefaultOptionUid: '',
    UseCarrierMemberIDFormat: true,
    MemberIDFormat: '',
    UseCarrierGroupIDFormat: true,
    GroupIDFormat: '',
    ReimbursementRuleUid: '',
    UseCarrierAutoWriteoffCreditType: true,
    AutoWriteoffCreditTypeUid: '',
    UserCarrierReferralRequired: true,
    ReferralRequired: false,
    UseCarrierNoNetwork: true,
    NoNetwork: false,
    PayerTypologySourceUid: '',
    PreferredLaboratoriesUid: [],
    InsurancePlanChc: {
      FQHCCarrier: false,
      SFCarrier: false,
      SFFileClaims: false,
      AutoAdjustVisits: false,
      InsuredAlwaysSameAsPatient: false,
      MBAdvantageInsCarrier: false,
      ApprovalSettingUid: '',
      Secondary_BypassPrimaryFiling: false
    }
  };

  public contactInfoGroup: FormGroup = new FormGroup({});
  public settingsGroup: FormGroup = new FormGroup({});
  public clinicalGroup: FormGroup = new FormGroup({});
  public chcGroup: FormGroup = new FormGroup({});

  public listItemDefaultValidation: FormGroupValidationRules = {};

  public form = new FormGroup({
    contactInfo: this.contactInfoGroup,
    settings: this.settingsGroup,
    clinical: this.clinicalGroup,
    chc: this.chcGroup,
  });

  override ngOnInit() {
    super.ngOnInit();
    // Load List Item and set default validation rules
    this.loadListItem(this.listItemDefaultValidation);
  }

  public setForm(): void {
    this.validateForm();
  }

  public getFormValidationErrors(formGroup: FormGroupArray): number {
    return FcListValidationService.getFormValidationErrors(formGroup, this.isSubmitted);
  }

  public override save(): void {
    const values = this.form.getRawValue();
    this.isSubmitted = true;
    const insuredAlwaysSameAsPatient = values.chc.medicareAdvantageCarrierl ? true : values.chc.insuredAlwaysSameAsPatient;

    const modifyItemDto: FcModifyInsurancePlanListItemDto = {
      // contactInfo Group part 1
      ExternalID: values.contactInfo.externalId,
      Name: values.contactInfo.name,
      Inactive: values.contactInfo.inactive,
      Notes: values.contactInfo.notes,
      InsuranceCarrierUid: this.parseFindControlValue(values.contactInfo.insuranceCarrierUid),
      UseCarrierAddress: values.contactInfo.useCarrierAddress,
      Address: {
        Address1: values.contactInfo?.address?.addressOne,
        Address2: values.contactInfo?.address?.addressTwo,
        City: values.contactInfo?.address?.city,
        State: values.contactInfo?.address?.state,
        Zip: values.contactInfo?.address?.zip,
        Country: values.contactInfo?.address?.country,
        CountyName: values.contactInfo?.address?.countyName,
        CountyCode: values.contactInfo?.address?.countyCode,
      },

      // settings Group
      UseInsuranceCarrierAccountType: values.settings.useInsuranceCarrierAccountType,
      AccountTypeUid: this.parseFindControlValue(values.settings.accountTypeUid),
      UseCarrierFeeSchedule: values.settings.useCarrierFeeSchedule,
      UseProviderFeeSchedule: values.settings.useProviderFeeSchedule,
      FeeScheduleUid: this.parseFindControlValue(values.settings.feeScheduleUid),
      UseCarrierAmountAllowedFeeSchedule: values.settings.useCarrierAmountAllowedFeeSchedule,
      AmountAllowedFeeScheduleUid: this.parseFindControlValue(values.settings.amountAllowedFeeScheduleUid),
      UseCarrierPaperClaimFormat: values.settings.useCarrierPaperClaimFormat,
      PaperClaimFormatUid: this.parseFindControlValue(values.settings.paperClaimFormatUid),
      UseCarrierElectronicClaimFormat: values.settings.useCarrierElectronicClaimFormat,
      ElectronicClaimFormatUid: this.parseFindControlValue(values.settings.electronicClaimFormatUid),
      UseCarrierPaperClaimDefaultOption: values.settings.useCarrierPaperClaimDefaultOption,
      PaperClaimDefaultOptionUid: this.parseFindControlValue(values.settings.paperClaimDefaultOptionUid),
      UseCarrierMemberIDFormat: values.settings.useCarrierMemberIDFormat,
      MemberIDFormat: values.settings.memberIdFormat,
      UseCarrierGroupIDFormat: values.settings.useCarrierGroupIDFormat,
      GroupIDFormat: values.settings.groupIdFormat,
      ReimbursementRuleUid: this.parseFindControlValue(values.settings.reimbursementRuleUid),
      UseCarrierAutoWriteoffCreditType: values.settings.useCarrierAutoWriteoffCreditType,
      AutoWriteoffCreditTypeUid: this.parseFindControlValue(values.settings.autoWriteoffCreditTypeUid),
      UserCarrierReferralRequired: values.settings.userCarrierReferralRequired,
      ReferralRequired: values.settings.referralRequired,
      UseCarrierNoNetwork: values.settings.useCarrierNoNetwork,
      NoNetwork: values.settings.noNetwork,

      // clinical Group
      PayerTypologySourceUid: this.parseFindControlValue(values.clinical.paymentTypologyUid),
      PreferredLaboratoriesUid: this.parseFindControlValue(values.clinical.preferredLaboratoriesUid, true),

      // chc Group
      InsurancePlanChc: {
        FQHCCarrier: values.chc.federallyQualifiedHealthCenter,
        SFCarrier: values.chc.slidingFeeCarrier,
        SFFileClaims: values.chc.fileClaims,
        AutoAdjustVisits: values.chc.autoAdjustVisits,
        InsuredAlwaysSameAsPatient: insuredAlwaysSameAsPatient,
        MBAdvantageInsCarrier: values.chc.medicareAdvantageCarrier,
        ApprovalSettingUid: this.parseFindControlValue(values.chc.chcSettingsUid),
        Secondary_BypassPrimaryFiling: values.chc.bypassPrimaryFiling,
      }
    };

    // contactInfo Group part 2 - phones
    if (values.contactInfo?.phoneOne?.phoneNumber
      && values.contactInfo?.phoneOne?.phoneType
      && values.contactInfo?.phoneOne?.phoneTypeUid) {
      modifyItemDto.Phone1 = {
        PhoneNumber: values.contactInfo.phoneOne.phoneNumber,
        PhoneType: values.contactInfo.phoneOne.phoneType,
        PhoneTypeUid: this.parseFindControlValue(values.contactInfo.phoneOne.phoneTypeUid),
      };
    }
    if (values.contactInfo?.phoneTwo?.phoneNumber
      && values.contactInfo?.phoneTwo?.phoneType
      && values.contactInfo?.phoneTwo?.phoneTypeUid) {
      modifyItemDto.Phone2 = {
        PhoneNumber: values.contactInfo.phoneTwo.phoneNumber,
        PhoneType: values.contactInfo.phoneTwo.phoneType,
        PhoneTypeUid: this.parseFindControlValue(values.contactInfo.phoneTwo.phoneTypeUid),
      };
    }
    if (values.contactInfo?.phoneThree?.phoneNumber
      && values.contactInfo?.phoneThree?.phoneType
      && values.contactInfo?.phoneThree?.phoneTypeUid) {
      modifyItemDto.Phone3 = {
        PhoneNumber: values.contactInfo.phoneThree.phoneNumber,
        PhoneType: values.contactInfo.phoneThree.phoneType,
        PhoneTypeUid: this.parseFindControlValue(values.contactInfo.phoneThree.phoneTypeUid),
      };
    }
    if (values.contactInfo?.phoneFour?.phoneNumber
      && values.contactInfo?.phoneFour?.phoneType
      && values.contactInfo?.phoneFour?.phoneTypeUid) {
      modifyItemDto.Phone4 = {
        PhoneNumber: values.contactInfo.phoneFour.phoneNumber,
        PhoneType: values.contactInfo.phoneFour.phoneType,
        PhoneTypeUid: this.parseFindControlValue(values.contactInfo.phoneFour.phoneTypeUid),
      };
    }

    super.save<FcModifyInsurancePlanListItemDto>(modifyItemDto);
  }
}
